/**
 * Sustainability status (QP-179) — portfolio-scoped self-contained component,
 * dispatched from the advisor's PortfolioPageRenderer. The public
 * PublicPortfolioPageRenderer has no `sustainability_status` case, so the
 * client-facing flow does not show it today.
 *
 * Renders the active portfolio's frozen sustainability matching snapshot. When
 * `allowOverride` is true (default), a deviation exposes the Accept /
 * Do-not-proceed decision controls; when false, no controls are shown.
 *
 * A read-only public branch (picked when `usePublicSession().isPublic` is true)
 * is kept for when the public renderer wires this component in. Because React
 * hooks can't be called conditionally, the two data paths live in sibling
 * sub-components.
 */

import type { SustainabilityDecisionValue } from "@repo/shared/types";
import { usePublicSession } from "@/lib/context/public-session-context";
import {
  usePortfolioSustainabilityState,
  useSavePortfolioSustainabilityDecision,
} from "@/lib/hooks/use-flow-instances/use-sustainability";
import { usePublicPortfolioSustainabilityState } from "@/lib/hooks/use-public-flow/use-sustainability";
import { SustainabilityStatusWidget } from "../sustainability-status-widget";

type SustainabilityStatusConfig = {
  style?: "none" | "border" | "box";
  title?: string;
  description?: string;
  allowOverride?: boolean;
};

type SustainabilityStatusProps = {
  /** The active portfolio this page is rendered for. */
  portfolioId: string;
  /** The advisor flow instance id (null / unused on the public surface). */
  instanceId?: string | null;
  config: SustainabilityStatusConfig;
  language: string;
  disabled?: boolean;
  hasValidationError?: boolean;
};

export function SustainabilityStatus({
  portfolioId,
  instanceId,
  config,
  language,
  disabled = false,
  hasValidationError,
}: SustainabilityStatusProps) {
  const { isPublic } = usePublicSession();
  // allowOverride defaults to true when unset (back-compat with existing configs).
  const allowOverride = config.allowOverride !== false;

  if (isPublic) {
    return (
      <PublicSustainabilityStatus
        portfolioId={portfolioId}
        config={config}
        language={language}
        allowOverride={allowOverride}
        hasValidationError={hasValidationError}
      />
    );
  }

  return (
    <AdvisorSustainabilityStatus
      portfolioId={portfolioId}
      instanceId={instanceId ?? null}
      config={config}
      language={language}
      disabled={disabled}
      allowOverride={allowOverride}
      hasValidationError={hasValidationError}
    />
  );
}

function AdvisorSustainabilityStatus({
  portfolioId,
  instanceId,
  config,
  language,
  disabled,
  allowOverride,
  hasValidationError,
}: {
  portfolioId: string;
  instanceId: string | null;
  config: SustainabilityStatusConfig;
  language: string;
  disabled: boolean;
  allowOverride: boolean;
  hasValidationError?: boolean;
}) {
  const { state, isLoading } = usePortfolioSustainabilityState(instanceId, portfolioId);
  const saveDecision = useSavePortfolioSustainabilityDecision(instanceId ?? "", portfolioId);

  const handleDecide = (decision: SustainabilityDecisionValue, justification: string) => {
    saveDecision.mutate({ decision, justification: justification.trim() || undefined });
  };

  return (
    <SustainabilityStatusWidget
      state={state}
      isLoading={isLoading}
      canDecide={!disabled}
      allowOverride={allowOverride}
      onDecide={handleDecide}
      decisionPending={saveDecision.isPending}
      style={config.style}
      title={config.title}
      description={config.description}
      language={language}
      hasValidationError={hasValidationError}
    />
  );
}

function PublicSustainabilityStatus({
  portfolioId,
  config,
  language,
  allowOverride,
  hasValidationError,
}: {
  portfolioId: string;
  config: SustainabilityStatusConfig;
  language: string;
  allowOverride: boolean;
  hasValidationError?: boolean;
}) {
  const { state, isLoading } = usePublicPortfolioSustainabilityState(portfolioId);

  return (
    <SustainabilityStatusWidget
      state={state}
      isLoading={isLoading}
      canDecide={false}
      allowOverride={allowOverride}
      style={config.style}
      title={config.title}
      description={config.description}
      language={language}
      hasValidationError={hasValidationError}
    />
  );
}
